"use client";

import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";

import cls from "./Header.module.css";
import { StickIcon } from "../../utils/images/MenuIcon";
import { ArrowPopoverIcon } from "@/utils/images/Arrows";
import { Button } from "../UI/Button/Button";
import { HeaderPopover } from "../UI/HeaderPopover/HeaderPopover";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MAIN_NAVIGATION_LINKS, HEADER_LINKS } from "@/utils/navLinks";
import { CALENDAR_URL } from "@/utils/consts";
import Image from "next/image";


export const Header = () => {
    const [open, setOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isOpenPopover, setIsOpenPopover] = useState(false);

    const isMobile = useIsMobile(767);
    const pathname = usePathname();

    const logoButtonRef = useRef<HTMLButtonElement>(null);

    const isCurrentPage = (href: string) => pathname === href;


    const togglePopover = () => {
        setIsOpenPopover((prev) => !prev);
    };

    const toggleMenu = () => {
        if (open) {
            setIsClosing(true);
        } else {
            setOpen(true);
            setIsClosing(false);
        }
    };

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                setOpen(false);
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing]);

    useEffect(() => {
        if (open) {
            const scrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = "100%";
            document.body.style.overflow = "hidden";
            document.body.style.scrollBehavior = "none";

            return () => {
                document.body.style.position = "";
                document.body.style.top = "";
                document.body.style.width = "";
                document.body.style.overflow = "";
                document.body.style.scrollBehavior = "smooth";
                window.scrollTo(0, scrollY);
            };
        }
    }, [open]);

    return (
        <header className={`${cls.header} ${open ? cls.open : ""}`}>
            <section className={cls.headerContainer}>
                <button
                    ref={logoButtonRef}
                    className={cls.logo}
                    onClick={togglePopover}
                    aria-label="Toggle popover"
                >
                    <Image src="/logo.svg" alt="UData Logo" width={150} height={50} className={cls.logoIcon} />
                    <ArrowPopoverIcon
                        className={`${cls.arrowPopoverIcon} ${isOpenPopover ? cls.open : ""}`}
                    />
                    <HeaderPopover
                        links={HEADER_LINKS}
                        isOpen={isOpenPopover}
                        onClose={() => setIsOpenPopover(false)}
                        triggerRef={logoButtonRef}
                    />
                </button>

                <nav className={`${cls.nav} ${open ? cls.open : ""} ${isClosing ? cls.closing : ""}`}>
                    <ul>
                        {MAIN_NAVIGATION_LINKS.filter(link => link.header !== false).map((link) => (
                            <li key={link.name}>
                                <Link
                                    href={link.href}
                                    className={isCurrentPage(link.href) ? cls.activeLink : ""}
                                    prefetch={link.prefetch}
                                    onClick={() => {
                                        if (open) {
                                            setIsClosing(true);
                                        }
                                    }}
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {isMobile && (
                        <div className={cls.mobileCallBtnNav}>
                            <Button href={CALENDAR_URL}>
                                Discuss a Project
                            </Button>
                        </div>
                    )}
                </nav>

                <div className={cls.rightSection}>
                    {!isMobile && (
                        <Button href={CALENDAR_URL} className={cls.callBtnNav}>
                            Discuss a Project
                        </Button>
                    )}

                    <button
                        className={`${cls.burger} ${open ? cls.open : ""}`}
                        onClick={toggleMenu}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        <div className={`${cls.stickIcons} ${open ? cls.open : ""} ${isClosing ? cls.closing : ""}`}>
                            <StickIcon className={`${cls.stickIcon} ${cls.one}`} />
                            <StickIcon className={`${cls.stickIcon} ${cls.two}`} />
                            <StickIcon className={`${cls.stickIcon} ${cls.three}`} />
                        </div>
                    </button>
                </div>
            </section>
        </header>
    );
};
